import {Platform} from 'react-native';
import RNFS from 'react-native-fs';
import {buildAttachmentAuthHeaders, buildAttachmentDownloadUrl, buildChatAvatarDownloadUrl, buildLinkPreviewImageUrl, buildUserAvatarDownloadUrl, fetchAttachmentDirectSource} from '../api/client';
import {forgetAnimatedAvatarBlob, rememberAnimatedAvatarBlob} from './animatedAvatarSource';
import {isLinkPreviewImagePath} from './linkPreviewImages';

/**
 * Persistent media URI cache, scoped to the JS module lifetime.
 *
 * Why: until we move attachments out of the `ciphertext` field on the server,
 * every video / image message arrives as a multi-MB base64 string inside the
 * message itself. Decoding and (especially) writing that base64 to disk on
 * every component mount is what makes ChatScreen feel like it "hangs" after
 * a video is sent — FlatList virtualization remounts the row, screen refocus
 * recreates the whole list, and each event would otherwise re-run a 100 MB
 * `RNFS.writeFile`.
 *
 * The cache keeps:
 *  - image `data:` URIs as strings (cheap to keep, but expensive to rebuild)
 *  - video file URIs (native) and blob object URLs (web)
 *
 * Files written to `RNFS.CachesDirectoryPath` survive app restarts, so the
 * `RNFS.exists` short-circuit keeps the cache effective across sessions even
 * though the in-memory Map is empty after a cold start.
 */

const MAX_IMAGE_DATA_URI_ENTRIES = 80;
const MAX_VIDEO_URI_ENTRIES = 8;
const MAX_AUDIO_URI_ENTRIES = 8;
const VIDEO_FILE_PREFIX = 'vrm-video-';

const imageFullDataUriCache = new Map<string, string>();
const imageThumbDataUriCache = new Map<string, string>();
const videoUriCache = new Map<string, string>();
const videoUriPromiseCache = new Map<string, Promise<string>>();
const webBlobUrlByMessageId = new Map<string, string>();
const audioBlobUrlByMessageId = new Map<string, string>();

function lruSet<K, V>(map: Map<K, V>, key: K, value: V, max: number): void {
  if (map.has(key)) {
    map.delete(key);
  }
  map.set(key, value);
  while (map.size > max) {
    const oldest = map.keys().next().value as K | undefined;
    if (oldest === undefined) break;
    map.delete(oldest);
  }
}

function lruTouch<K, V>(map: Map<K, V>, key: K): V | undefined {
  const value = map.get(key);
  if (value === undefined) return undefined;
  map.delete(key);
  map.set(key, value);
  return value;
}

function normalizeBase64(b: string): string {
  return b.replace(/\s+/g, '');
}

function normalizeImageMime(mimeType: string): string {
  const [base] = mimeType.split(';');
  return base || 'image/jpeg';
}

function normalizeVideoMime(mimeType: string): string {
  const [base] = mimeType.split(';');
  return base || 'video/mp4';
}

function extensionForVideoMime(mimeType: string): string {
  const m = mimeType.toLowerCase();
  if (m.includes('webm')) return 'webm';
  if (m.includes('mp4')) return 'mp4';
  if (m.includes('quicktime')) return 'mov';
  return 'mp4';
}

function safeFileToken(messageId: string): string {
  return messageId.replace(/[^a-zA-Z0-9_-]/g, '_');
}

function buildDataUri(mimeType: string, normalizedBase64: string): string {
  return `data:${mimeType};base64,${normalizedBase64}`;
}

/**
 * Return a stable `data:` URI for the full-resolution image bound to
 * `messageId`. The string is computed once and reused on subsequent calls.
 * Reusing the same string reference also helps React Native `<Image>` keep
 * its decoded bitmap cached instead of redecoding on every row remount.
 */
export function getCachedImageDataUri(
  messageId: string,
  mimeType: string,
  imageBase64: string,
): string {
  const cached = lruTouch(imageFullDataUriCache, messageId);
  if (cached !== undefined) return cached;
  const uri = buildDataUri(normalizeImageMime(mimeType), normalizeBase64(imageBase64));
  lruSet(imageFullDataUriCache, messageId, uri, MAX_IMAGE_DATA_URI_ENTRIES);
  return uri;
}

/**
 * Return a stable `data:` URI for the thumbnail (or the full image when no
 * thumbnail was sent). Cached independently from the full-resolution URI so
 * we never collapse them into the same string.
 */
export function getCachedImageThumbDataUri(
  messageId: string,
  mimeType: string,
  thumbnailBase64: string | undefined,
  fallbackImageBase64: string,
): string {
  const cached = lruTouch(imageThumbDataUriCache, messageId);
  if (cached !== undefined) return cached;
  const source =
    typeof thumbnailBase64 === 'string' && thumbnailBase64.length > 0
      ? thumbnailBase64
      : fallbackImageBase64;
  const uri = buildDataUri(normalizeImageMime(mimeType), normalizeBase64(source));
  lruSet(imageThumbDataUriCache, messageId, uri, MAX_IMAGE_DATA_URI_ENTRIES);
  return uri;
}

function base64ToUint8Array(base64: string): Uint8Array {
  const binary = globalThis.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function buildWebVideoObjectUrl(mimeType: string, base64: string): string {
  const bytes = base64ToUint8Array(base64);
  const part = bytes as unknown as BlobPart;
  const blob = new Blob([part], { type: normalizeVideoMime(mimeType) });
  return URL.createObjectURL(blob);
}

function videoFilePathFor(messageId: string, mimeType: string): string | null {
  const cacheDir = RNFS.CachesDirectoryPath;
  if (!cacheDir) return null;
  return `${cacheDir}/${VIDEO_FILE_PREFIX}${safeFileToken(messageId)}.${extensionForVideoMime(mimeType)}`;
}

async function prepareVideoFileNative(
  messageId: string,
  mimeType: string,
  videoBase64: string,
): Promise<string> {
  const path = videoFilePathFor(messageId, mimeType);
  if (!path) {
    throw new Error('Caches directory unavailable');
  }
  let exists: boolean;
  try {
    exists = await RNFS.exists(path);
  } catch {
    exists = false;
  }
  if (!exists) {
    const normalized = normalizeBase64(videoBase64);
    await RNFS.writeFile(path, normalized, 'base64');
  }
  return path.startsWith('file://') ? path : `file://${path}`;
}

/**
 * Synchronously inspect the in-memory cache. Returns the resolved URI when we
 * already have a hot entry, otherwise `null` and the caller must invoke
 * {@link getOrPrepareVideoUri}.
 */
export function peekCachedVideoUri(messageId: string): string | null {
  return lruTouch(videoUriCache, messageId) ?? null;
}

/**
 * Build (or return from cache) a playable URI for the given video message.
 *
 * - On native this is a `file://…` path inside `caches/`. The file is written
 *   only on the very first call for this `messageId`; subsequent calls in the
 *   same session resolve from the in-memory Map, and even after a cold start
 *   the on-disk file is reused via `RNFS.exists`.
 * - On web this is a `blob:` URL backed by a `Blob` built from the base64.
 *
 * Multiple concurrent calls for the same `messageId` share one promise so we
 * never write the same 100 MB twice.
 */
export function getOrPrepareVideoUri(
  messageId: string,
  mimeType: string,
  videoBase64: string,
): Promise<string> {
  const hot = lruTouch(videoUriCache, messageId);
  if (hot !== undefined) return Promise.resolve(hot);
  const inflight = videoUriPromiseCache.get(messageId);
  if (inflight) return inflight;

  const promise = (async () => {
    if (Platform.OS === 'web') {
      const url = buildWebVideoObjectUrl(mimeType, normalizeBase64(videoBase64));
      webBlobUrlByMessageId.set(messageId, url);
      return url;
    }
    return prepareVideoFileNative(messageId, mimeType, videoBase64);
  })();

  videoUriPromiseCache.set(messageId, promise);
  return promise
    .then((uri) => {
      if (videoUriCache.size >= MAX_VIDEO_URI_ENTRIES) {
        const oldestKey = videoUriCache.keys().next().value as string | undefined;
        if (oldestKey !== undefined && oldestKey !== messageId) {
          evictVideoUri(oldestKey);
        }
      }
      videoUriCache.set(messageId, uri);
      return uri;
    })
    .finally(() => {
      videoUriPromiseCache.delete(messageId);
    });
}

/**
 * Best-effort fallback: build a `data:video/...;base64,…` URI without touching
 * the disk. Useful when the file-based path fails on a given device. The
 * result is NOT cached because data URIs for video are huge and the caller
 * usually has the base64 string already in scope.
 */
export function buildVideoDataUri(mimeType: string, videoBase64: string): string {
  return buildDataUri(normalizeVideoMime(mimeType), normalizeBase64(videoBase64));
}

/**
 * Голосовое из старого (v1) сообщения в вебе: `blob:`-ссылка на инлайновые байты.
 *
 * <p>Нативный путь пишет их во временный файл и отдаёт плееру `file://…`. В браузере
 * файла нет — веб-шим `react-native-fs` кладёт base64 в память, и такой путь плееру
 * скормить нечем. Ссылка кешируется по сообщению: без этого каждое нажатие «play»
 * оставляло бы в памяти вкладки ещё одну копию записи.
 */
export function getOrPrepareWebAudioUri(
  messageId: string,
  mimeType: string,
  audioBase64: string,
): string {
  const hot = audioBlobUrlByMessageId.get(messageId);
  if (hot !== undefined) return hot;
  const bytes = base64ToUint8Array(normalizeBase64(audioBase64));
  const part = bytes as unknown as BlobPart;
  const [base] = mimeType.split(';');
  const blob = new Blob([part], { type: base || 'audio/mp4' });
  const url = URL.createObjectURL(blob);
  if (audioBlobUrlByMessageId.size >= MAX_AUDIO_URI_ENTRIES) {
    const oldest = audioBlobUrlByMessageId.keys().next().value as string | undefined;
    if (oldest !== undefined) evictWebAudioUri(oldest);
  }
  audioBlobUrlByMessageId.set(messageId, url);
  return url;
}

export function evictWebAudioUri(messageId: string): void {
  const url = audioBlobUrlByMessageId.get(messageId);
  if (!url) return;
  audioBlobUrlByMessageId.delete(messageId);
  try {
    URL.revokeObjectURL(url);
  } catch {
    // ignore: blob URL might be invalid in some environments
  }
}

function evictVideoUri(messageId: string): void {
  const blobUrl = webBlobUrlByMessageId.get(messageId);
  if (blobUrl) {
    try {
      URL.revokeObjectURL(blobUrl);
    } catch {
      // ignore: blob URL might be invalid in some environments
    }
    webBlobUrlByMessageId.delete(messageId);
  }
  const uri = videoUriCache.get(messageId);
  videoUriCache.delete(messageId);
  videoUriPromiseCache.delete(messageId);
  if (uri && Platform.OS !== 'web') {
    const fsPath = uri.startsWith('file://') ? uri.slice('file://'.length) : uri;
    void RNFS.unlink(fsPath).catch(() => {});
  }
}

// ---------------------------------------------------------------------------
// v2 attachment cache (downloads from /api/attachments/{id})
//
// The semantics mirror the existing video file cache but the "source of
// truth" is a remote URL instead of an inline base64 string. Each attachment
// id is downloaded at most once per session (and at most once across cold
// starts because the file persists under `caches/`).
// ---------------------------------------------------------------------------

const MAX_ATTACHMENT_FILE_ENTRIES = 16;
const ATTACHMENT_FILE_PREFIX = 'vrm-att-';

const attachmentFileUriCache = new Map<string, string>();
const attachmentFileUriPromiseCache = new Map<string, Promise<string>>();
const attachmentBlobUrlByKey = new Map<string, string>();

function attachmentCacheKey(attachmentId: string, mimeType: string | undefined | null): string {
  const ext = mimeType ? extensionForGenericMime(mimeType) : 'bin';
  return `${safeFileToken(attachmentId)}.${ext}`;
}

function extensionForGenericMime(mimeType: string): string {
  const m = mimeType.toLowerCase();
  if (m.includes('webm')) return 'webm';
  if (m.includes('mp4')) return 'mp4';
  if (m.includes('quicktime')) return 'mov';
  if (m.includes('mpeg')) return 'mp3';
  if (m.includes('ogg')) return 'ogg';
  if (m.includes('aac')) return 'aac';
  if (m.includes('m4a')) return 'm4a';
  if (m.includes('jpeg') || m.includes('jpg')) return 'jpg';
  if (m.includes('png')) return 'png';
  if (m.includes('webp')) return 'webp';
  if (m.includes('gif')) return 'gif';
  return 'bin';
}

export function peekCachedAttachmentUri(attachmentId: string): string | null {
  return lruTouch(attachmentFileUriCache, attachmentId) ?? null;
}

/**
 * Забыть запись и скачать заново. Отдельной функцией, а не рекурсией внутри
 * {@link getOrDownloadAttachmentUri}: рекурсия по тому же пути ушла бы в цикл, если файл
 * пропадает сразу после скачивания.
 */
function refetchAttachmentUri(attachmentId: string, mimeType: string): Promise<string> {
  attachmentFileUriCache.delete(attachmentId);
  return getOrDownloadAttachmentUri(attachmentId, mimeType);
}

/**
 * Download the bytes for a v2 attachment and return a playable URI:
 * - on native: a `file://…` path written into `caches/`. Subsequent calls in
 *   the same session resolve from the in-memory Map; after a cold start the
 *   file is reused via `RNFS.exists`.
 * - on web: a `blob:` URL kept in memory until evicted.
 *
 * Concurrent calls for the same id share a single in-flight promise.
 */
export function getOrDownloadAttachmentUri(
  attachmentId: string,
  mimeType: string,
): Promise<string> {
  const hot = lruTouch(attachmentFileUriCache, attachmentId);
  if (hot !== undefined) {
    // Запись в памяти есть — но файл на диске мог исчезнуть. Мы такое уже ловили: экран чата
    // удалял этот файл как свой временный (см. `startAudioPlayback`), и кэш продолжал отдавать
    // путь в никуда, пока запись не вытеснит LRU. Даже без той ошибки `caches/` чистит система
    // при нехватке места, и путь из кэша не гарантия существования файла.
    if (Platform.OS === 'web') return Promise.resolve(hot);
    const filePath = hot.replace(/^file:\/\//, '');
    return RNFS.exists(filePath)
      .then((present) => (present ? hot : refetchAttachmentUri(attachmentId, mimeType)))
      .catch(() => hot);
  }
  const inflight = attachmentFileUriPromiseCache.get(attachmentId);
  if (inflight) return inflight;

  const url = buildAttachmentDownloadUrl(attachmentId);

  const promise = (async () => {
    const headers = await buildAttachmentAuthHeaders();
    if (Platform.OS === 'web') {
      // Force same-origin streaming: a browser fetch() would otherwise follow the
      // API's 302 to the R2 host, which sends no Access-Control-Allow-Origin and
      // gets blocked by CORS. ?stream=1 makes the API return the bytes directly.
      const res = await fetch(`${url}?stream=1`, { headers });
      if (!res.ok) throw new Error(`Attachment fetch failed: ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      attachmentBlobUrlByKey.set(attachmentId, objectUrl);
      return objectUrl;
    }
    const cacheDir = RNFS.CachesDirectoryPath;
    if (!cacheDir) {
      throw new Error('Caches directory unavailable');
    }
    let path = `${cacheDir}/${ATTACHMENT_FILE_PREFIX}${attachmentCacheKey(attachmentId, mimeType)}`;
    let exists: boolean;
    try {
      exists = await RNFS.exists(path);
    } catch {
      exists = false;
    }
    if (!exists) {
      // Native download must NOT follow the API's 302 redirect to R2 with the
      // Authorization header attached — R2 rejects a pre-signed URL that also
      // carries an Authorization header with HTTP 400. Resolve the pre-signed
      // URL first (with auth) and download it directly without auth. When the
      // server has no pre-signing (returns null), fall back to streaming through
      // our API with auth (that path returns bytes directly, no redirect).
      let fromUrl = url;
      let downloadHeaders: Record<string, string> = headers;
      try {
        const direct = await fetchAttachmentDirectSource(attachmentId);
        if (direct.url) {
          fromUrl = direct.url;
          downloadHeaders = {};
        }
        // The attachment row, not the message payload, decides the format. A
        // voice note recorded in a browser was stored as WebM/Opus and may since
        // have been converted to AAC by the backfill; naming the cache file from
        // the stale payload mime gives iOS a `.webm` file full of AAC, which it
        // refuses to play. Recompute only here, so the cached fast path above
        // still costs no network call.
        if (direct.mimeType) {
          const corrected = `${cacheDir}/${ATTACHMENT_FILE_PREFIX}${attachmentCacheKey(
            attachmentId,
            direct.mimeType,
          )}`;
          if (corrected !== path) {
            path = corrected;
            try {
              if (await RNFS.exists(path)) {
                return path.startsWith('file://') ? path : `file://${path}`;
              }
            } catch {
              /* fall through to download */
            }
          }
        }
      } catch {
        /* fall back to the streaming endpoint with auth */
      }
      const downloaded = await RNFS.downloadFile({
        fromUrl,
        toFile: path,
        headers: downloadHeaders,
        background: false,
        // Android OkHttp defaults can be too short for large media files.
        connectionTimeout: 30_000,
        readTimeout: 10 * 60 * 1000,
      }).promise;
      if (downloaded.statusCode < 200 || downloaded.statusCode >= 300) {
        try { await RNFS.unlink(path); } catch { /* ignore */ }
        throw new Error(`Attachment fetch failed: ${downloaded.statusCode}`);
      }
    }
    return path.startsWith('file://') ? path : `file://${path}`;
  })();

  attachmentFileUriPromiseCache.set(attachmentId, promise);
  return promise
    .then((uri) => {
      if (attachmentFileUriCache.size >= MAX_ATTACHMENT_FILE_ENTRIES) {
        const oldestKey = attachmentFileUriCache.keys().next().value as string | undefined;
        if (oldestKey !== undefined && oldestKey !== attachmentId) {
          evictAttachmentUri(oldestKey);
        }
      }
      attachmentFileUriCache.set(attachmentId, uri);
      return uri;
    })
    .finally(() => {
      attachmentFileUriPromiseCache.delete(attachmentId);
    });
}

function evictAttachmentUri(attachmentId: string): void {
  const blob = attachmentBlobUrlByKey.get(attachmentId);
  if (blob) {
    try {
      URL.revokeObjectURL(blob);
    } catch {
      // ignore
    }
    attachmentBlobUrlByKey.delete(attachmentId);
  }
  const uri = attachmentFileUriCache.get(attachmentId);
  attachmentFileUriCache.delete(attachmentId);
  attachmentFileUriPromiseCache.delete(attachmentId);
  if (uri && Platform.OS !== 'web' && uri.startsWith('file://')) {
    const fsPath = uri.slice('file://'.length);
    void RNFS.unlink(fsPath).catch(() => {});
  }
}

// ---------------------------------------------------------------------------
// User avatars (GET /api/users/{id}/avatar?v=…)
// ---------------------------------------------------------------------------

const MAX_AVATAR_FILE_ENTRIES = 40;
const AVATAR_FILE_PREFIX = 'vrm-av-';

/**
 * Расширение кэшированного аватара — не украшение, а признак формата.
 *
 * <p>Аватар может быть живым (анимированный GIF): такой рисуется другим слоем, чем обычный
 * снимок, и решение принимается синхронно, в момент отрисовки бейджа — по имени файла.
 * Раньше всё сохранялось как `.jpg` независимо от содержимого, и анимация была неотличима
 * от фотографии.
 */
const AVATAR_CACHE_EXTS = ['gif', 'jpg'] as const;

function toFileUri(path: string): string {
  return path.startsWith('file://') ? path : `file://${path}`;
}

/** Уже скачанный файл под любым из известных расширений. */
async function findCachedAvatarFile(base: string): Promise<string | null> {
  for (const ext of AVATAR_CACHE_EXTS) {
    const candidate = `${base}.${ext}`;
    try {
      if (await RNFS.exists(candidate)) return candidate;
    } catch {
      /* нет доступа к файлу — считаем, что его нет */
    }
  }
  return null;
}

/**
 * Довести скачанный файл до имени с правильным расширением.
 *
 * <p>Формат определяется по первым байтам, а не по заголовку ответа: `downloadFile` их не
 * отдаёт, а верить расширению в ссылке тут нечему — адрес аватара его не содержит вовсе.
 */
async function finalizeAvatarDownload(base: string, tmpPath: string): Promise<string> {
  let ext: string = 'jpg';
  try {
    const head = await RNFS.read(tmpPath, 6, 0, 'ascii');
    if (typeof head === 'string' && /^GIF8[79]a/.test(head)) ext = 'gif';
  } catch {
    /* не прочиталось — обычный снимок, самый частый случай */
  }
  const target = `${base}.${ext}`;
  try {
    if (await RNFS.exists(target)) await RNFS.unlink(target);
  } catch {
    /* перезапишем ниже, а если и там не выйдет — вернём временный путь */
  }
  try {
    await RNFS.moveFile(tmpPath, target);
  } catch {
    return toFileUri(tmpPath);
  }
  return toFileUri(target);
}

const avatarFileUriCache = new Map<string, string>();
const avatarFileUriPromiseCache = new Map<string, Promise<string>>();
const avatarBlobUrlByKey = new Map<string, string>();

function avatarCacheKey(userId: string, revision: number): string {
  return `${userId}:${revision}`;
}

export function peekCachedAvatarUri(userId: string, revision: number): string | null {
  return lruTouch(avatarFileUriCache, avatarCacheKey(userId, revision)) ?? null;
}

export function getOrDownloadAvatarUri(userId: string, revision: number): Promise<string> {
  const key = avatarCacheKey(userId, revision);
  const hot = lruTouch(avatarFileUriCache, key);
  if (hot !== undefined) return Promise.resolve(hot);
  const inflight = avatarFileUriPromiseCache.get(key);
  if (inflight) return inflight;

  const url = buildUserAvatarDownloadUrl(userId, revision);

  const promise = (async () => {
    const headers = await buildAttachmentAuthHeaders();
    if (Platform.OS === 'web') {
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error(`Avatar fetch failed: ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      avatarBlobUrlByKey.set(key, objectUrl);
      // Единственное место, где ещё известен тип: дальше остаётся `blob:`-адрес без признаков.
      rememberAnimatedAvatarBlob(objectUrl, blob.type);
      return objectUrl;
    }
    const cacheDir = RNFS.CachesDirectoryPath;
    if (!cacheDir) {
      throw new Error('Caches directory unavailable');
    }
    const safeUser = userId.replace(/[^a-zA-Z0-9_-]/g, '_');
    const base = `${cacheDir}/${AVATAR_FILE_PREFIX}${safeUser}_r${revision}`;
    const cached = await findCachedAvatarFile(base);
    if (cached) {
      return toFileUri(cached);
    }
    const tmp = `${base}.part`;
    const downloaded = await RNFS.downloadFile({
      fromUrl: url,
      toFile: tmp,
      headers,
      background: false,
    }).promise;
    if (downloaded.statusCode < 200 || downloaded.statusCode >= 300) {
      try {
        await RNFS.unlink(tmp);
      } catch {
        /* ignore */
      }
      throw new Error(`Avatar fetch failed: ${downloaded.statusCode}`);
    }
    return finalizeAvatarDownload(base, tmp);
  })();

  avatarFileUriPromiseCache.set(key, promise);
  return promise
    .then((uri) => {
      if (avatarFileUriCache.size >= MAX_AVATAR_FILE_ENTRIES) {
        const oldestKey = avatarFileUriCache.keys().next().value as string | undefined;
        if (oldestKey !== undefined && oldestKey !== key) {
          evictAvatarUri(oldestKey);
        }
      }
      avatarFileUriCache.set(key, uri);
      return uri;
    })
    .finally(() => {
      avatarFileUriPromiseCache.delete(key);
    });
}

function evictAvatarUri(cacheKey: string): void {
  const blob = avatarBlobUrlByKey.get(cacheKey);
  if (blob) {
    try {
      URL.revokeObjectURL(blob);
    } catch {
      // ignore
    }
    forgetAnimatedAvatarBlob(blob);
    avatarBlobUrlByKey.delete(cacheKey);
  }
  const uri = avatarFileUriCache.get(cacheKey);
  avatarFileUriCache.delete(cacheKey);
  avatarFileUriPromiseCache.delete(cacheKey);
  if (uri && Platform.OS !== 'web' && uri.startsWith('file://')) {
    const fsPath = uri.slice('file://'.length);
    void RNFS.unlink(fsPath).catch(() => {});
  }
}

// ---------------------------------------------------------------------------
// Chat avatars (GET /api/chats/{id}/avatar?v=…)
// ---------------------------------------------------------------------------

const MAX_CHAT_AVATAR_FILE_ENTRIES = 40;
const CHAT_AVATAR_FILE_PREFIX = 'vrm-chav-';

const chatAvatarFileUriCache = new Map<string, string>();
const chatAvatarFileUriPromiseCache = new Map<string, Promise<string>>();
const chatAvatarBlobUrlByKey = new Map<string, string>();

function chatAvatarCacheKey(chatId: string, revision: number): string {
  return `${chatId}:${revision}`;
}

export function peekCachedChatAvatarUri(chatId: string, revision: number): string | null {
  return lruTouch(chatAvatarFileUriCache, chatAvatarCacheKey(chatId, revision)) ?? null;
}

export function getOrDownloadChatAvatarUri(chatId: string, revision: number): Promise<string> {
  const key = chatAvatarCacheKey(chatId, revision);
  const hot = lruTouch(chatAvatarFileUriCache, key);
  if (hot !== undefined) return Promise.resolve(hot);
  const inflight = chatAvatarFileUriPromiseCache.get(key);
  if (inflight) return inflight;

  const url = buildChatAvatarDownloadUrl(chatId, revision);

  const promise = (async () => {
    const headers = await buildAttachmentAuthHeaders();
    if (Platform.OS === 'web') {
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error(`Chat avatar fetch failed: ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      chatAvatarBlobUrlByKey.set(key, objectUrl);
      rememberAnimatedAvatarBlob(objectUrl, blob.type);
      return objectUrl;
    }
    const cacheDir = RNFS.CachesDirectoryPath;
    if (!cacheDir) {
      throw new Error('Caches directory unavailable');
    }
    const safeChat = chatId.replace(/[^a-zA-Z0-9_-]/g, '_');
    const base = `${cacheDir}/${CHAT_AVATAR_FILE_PREFIX}${safeChat}_r${revision}`;
    const cached = await findCachedAvatarFile(base);
    if (cached) {
      return toFileUri(cached);
    }
    const tmp = `${base}.part`;
    const downloaded = await RNFS.downloadFile({
      fromUrl: url,
      toFile: tmp,
      headers,
      background: false,
    }).promise;
    if (downloaded.statusCode < 200 || downloaded.statusCode >= 300) {
      try {
        await RNFS.unlink(tmp);
      } catch {
        /* ignore */
      }
      throw new Error(`Chat avatar fetch failed: ${downloaded.statusCode}`);
    }
    return finalizeAvatarDownload(base, tmp);
  })();

  chatAvatarFileUriPromiseCache.set(key, promise);
  return promise
    .then((uri) => {
      if (chatAvatarFileUriCache.size >= MAX_CHAT_AVATAR_FILE_ENTRIES) {
        const oldestKey = chatAvatarFileUriCache.keys().next().value as string | undefined;
        if (oldestKey !== undefined && oldestKey !== key) {
          evictChatAvatarUri(oldestKey);
        }
      }
      chatAvatarFileUriCache.set(key, uri);
      return uri;
    })
    .finally(() => {
      chatAvatarFileUriPromiseCache.delete(key);
    });
}

function evictChatAvatarUri(cacheKey: string): void {
  const blob = chatAvatarBlobUrlByKey.get(cacheKey);
  if (blob) {
    try {
      URL.revokeObjectURL(blob);
    } catch {
      // ignore
    }
    forgetAnimatedAvatarBlob(blob);
    chatAvatarBlobUrlByKey.delete(cacheKey);
  }
  const uri = chatAvatarFileUriCache.get(cacheKey);
  chatAvatarFileUriCache.delete(cacheKey);
  chatAvatarFileUriPromiseCache.delete(cacheKey);
  if (uri && Platform.OS !== 'web' && uri.startsWith('file://')) {
    const fsPath = uri.slice('file://'.length);
    void RNFS.unlink(fsPath).catch(() => {});
  }
}

// ---------------------------------------------------------------------------
// Картинки превью ссылок (GET /api/link-preview/image/<токен>, PRIV-4)
// ---------------------------------------------------------------------------
//
// Тем же путём, что и аватары: ручка закрыта входом, а `<Image>` с заголовком на вебе не
// работает. Чужой адрес сюда не пропускается вовсе — ради этого прокси и заведён.

const MAX_LINK_PREVIEW_IMAGE_ENTRIES = 60;
const LINK_PREVIEW_IMAGE_FILE_PREFIX = 'vrm-lp-';

const linkPreviewImageUriCache = new Map<string, string>();
const linkPreviewImagePromiseCache = new Map<string, Promise<string>>();
const linkPreviewImageBlobByPath = new Map<string, string>();

export function peekCachedLinkPreviewImageUri(proxyPath: string): string | null {
  return lruTouch(linkPreviewImageUriCache, proxyPath) ?? null;
}

export function getOrDownloadLinkPreviewImageUri(proxyPath: string): Promise<string> {
  if (!isLinkPreviewImagePath(proxyPath)) {
    return Promise.reject(new Error('Not a link preview image path'));
  }
  const url = buildLinkPreviewImageUrl(proxyPath);
  const hot = lruTouch(linkPreviewImageUriCache, proxyPath);
  if (hot !== undefined) return Promise.resolve(hot);
  const inflight = linkPreviewImagePromiseCache.get(proxyPath);
  if (inflight) return inflight;

  const promise = (async () => {
    const headers = await buildAttachmentAuthHeaders();
    if (Platform.OS === 'web') {
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error(`Link preview image fetch failed: ${res.status}`);
      const objectUrl = URL.createObjectURL(await res.blob());
      linkPreviewImageBlobByPath.set(proxyPath, objectUrl);
      return objectUrl;
    }
    const cacheDir = RNFS.CachesDirectoryPath;
    if (!cacheDir) {
      throw new Error('Caches directory unavailable');
    }
    // Токен — последний сегмент пути; кроме hex там ничего нет, это проверил разбор выше.
    const token = proxyPath.slice(proxyPath.lastIndexOf('/') + 1);
    const base = `${cacheDir}/${LINK_PREVIEW_IMAGE_FILE_PREFIX}${token}`;
    const cached = await findCachedAvatarFile(base);
    if (cached) {
      return toFileUri(cached);
    }
    const tmp = `${base}.part`;
    const downloaded = await RNFS.downloadFile({
      fromUrl: url,
      toFile: tmp,
      headers,
      background: false,
    }).promise;
    if (downloaded.statusCode < 200 || downloaded.statusCode >= 300) {
      try {
        await RNFS.unlink(tmp);
      } catch {
        /* ignore */
      }
      throw new Error(`Link preview image fetch failed: ${downloaded.statusCode}`);
    }
    return finalizeAvatarDownload(base, tmp);
  })();

  linkPreviewImagePromiseCache.set(proxyPath, promise);
  return promise
    .then((uri) => {
      if (linkPreviewImageUriCache.size >= MAX_LINK_PREVIEW_IMAGE_ENTRIES) {
        const oldestKey = linkPreviewImageUriCache.keys().next().value as string | undefined;
        if (oldestKey !== undefined && oldestKey !== proxyPath) {
          evictLinkPreviewImageUri(oldestKey);
        }
      }
      linkPreviewImageUriCache.set(proxyPath, uri);
      return uri;
    })
    .finally(() => {
      linkPreviewImagePromiseCache.delete(proxyPath);
    });
}

function evictLinkPreviewImageUri(proxyPath: string): void {
  const blob = linkPreviewImageBlobByPath.get(proxyPath);
  if (blob) {
    try {
      URL.revokeObjectURL(blob);
    } catch {
      // ignore
    }
    linkPreviewImageBlobByPath.delete(proxyPath);
  }
  const uri = linkPreviewImageUriCache.get(proxyPath);
  linkPreviewImageUriCache.delete(proxyPath);
  linkPreviewImagePromiseCache.delete(proxyPath);
  if (uri && Platform.OS !== 'web' && uri.startsWith('file://')) {
    void RNFS.unlink(uri.slice('file://'.length)).catch(() => {});
  }
}

/** Test / debug hook: drop everything. Not used in production code paths. */
export function __clearMediaCacheForTests(): void {
  imageFullDataUriCache.clear();
  imageThumbDataUriCache.clear();
  for (const id of Array.from(videoUriCache.keys())) {
    evictVideoUri(id);
  }
  videoUriPromiseCache.clear();
  for (const id of Array.from(audioBlobUrlByMessageId.keys())) {
    evictWebAudioUri(id);
  }
  attachmentFileUriCache.clear();
  attachmentFileUriPromiseCache.clear();
  for (const blob of Array.from(attachmentBlobUrlByKey.values())) {
    try {
      URL.revokeObjectURL(blob);
    } catch {
      // ignore
    }
  }
  attachmentBlobUrlByKey.clear();
  for (const k of Array.from(avatarFileUriCache.keys())) {
    evictAvatarUri(k);
  }
  avatarFileUriCache.clear();
  avatarFileUriPromiseCache.clear();
  for (const k of Array.from(chatAvatarFileUriCache.keys())) {
    evictChatAvatarUri(k);
  }
  chatAvatarFileUriCache.clear();
  chatAvatarFileUriPromiseCache.clear();
  for (const k of Array.from(linkPreviewImageUriCache.keys())) {
    evictLinkPreviewImageUri(k);
  }
  linkPreviewImageUriCache.clear();
  linkPreviewImagePromiseCache.clear();
}
